import { Entity, EntityBase, Var } from "addeus-common-library/stores/firestore";
import {
    Collection,
    SubCollection,
} from "addeus-common-library/stores/firestore/collection";
import {
    SecurityEntity,
    SecurityProperty,
    SecuritySubCollection,
} from "addeus-common-library/stores/firestore/security/securityDecorators";
import moment from "moment-with-locales-es6";
import type { Profile } from "./profile";
import "./workspace";
import type { Workspace } from "./workspace";

const isAdmin =
    "get(/databases/$(database)/documents/UserMetadata/$(request.auth.uid)).data.role == 'admin'";

export enum Role {
    admin = "admin",
    user = "user",
}

export class WorkspaceRight {
    role: Role = Role.user;
}

export class ProfileRight {
    role: Role = Role.user;
    user?: UserMetadata;
}

export class NotificationChannels extends EntityBase {
    @Var(Boolean)
    whatsapp: boolean = false;

    @Var(Boolean)
    email: boolean = true;
}

export class NotificationsSettings extends EntityBase {
    @Var(NotificationChannels)
    publications: NotificationChannels = new NotificationChannels();

    @Var(NotificationChannels)
    opportunities: NotificationChannels = new NotificationChannels();
}

@Collection({
    namespace: "UserMetadata",
})
@SecurityEntity({
    get: `${isAdmin} || request.auth.uid == UserMetadataId`,
    list: isAdmin,

    create: isAdmin,
    update: `${isAdmin} || request.auth.uid == UserMetadataId`,
    delete: "false",
})
export class UserMetadata extends Entity {
    @Var(String)
    firstName: string = "";

    @Var(String)
    lastName: string = "";

    @Var(String)
    email: string = "";

    @Var(String)
    phoneNumber: string = "";

    @Var(Role)
    @SecurityProperty({
        update: isAdmin,
    })
    role: Role = Role.user;

    @Var(moment)
    createdAt: moment = moment();

    @Var(moment)
    deletedAt: moment | null = null;

    @Var(String)
    picture: string | null = null;

    @Var(NotificationsSettings)
    notifications: NotificationsSettings = new NotificationsSettings();

    @Var(String)
    note?: string;

    @Var(String)
    locationName?: string;

    @Collection<Workspace>({
        namespace: "Workspace",
        backlistFields: {
            owners: true,
            users: true,
            profiles: true,
        },
    })
    @SecuritySubCollection({
        get: "request.auth.uid == UserMetadataId || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid))",
        list: "true",
        delete: "request.auth.uid == UserMetadataId || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid))",
    })
    userWorkspaces = new SubCollection<Workspace>();

    @Collection<Workspace>({
        namespace: "Workspace",
        backlistFields: {
            owners: true,
            users: true,
            profiles: true,
        },
    })
    @SecuritySubCollection({
        get: `${isAdmin} || request.auth.uid == UserMetadataId || exists(/databases/$(database)/documents/Workspace/$(WorkspaceId)/owners/$(request.auth.uid))`,
        list: "true",
        create: isAdmin,
        update: isAdmin,
        delete: `request.auth.uid == UserMetadataId || exists(/databases/$(database)/documents/Profile/$(ProfileId)/owners/$(request.auth.uid)) || ${isAdmin}`,
    })
    ownerWorkspaces = new SubCollection<Workspace>();

    @Collection<Profile>({
        namespace: "Profile",
        backlistFields: {
            owners: true,
            users: true,
            posts: true,
            tasks: true,
            socialProfiles: true,
            reviews: true,
        },
    })
    @SecuritySubCollection({
        get: "request.auth.uid == UserMetadataId",
        list: "true",
        delete: "request.auth.uid == UserMetadataId || exists(/databases/$(database)/documents/Profile/$(ProfileId)/owners/$(request.auth.uid))",
    })
    userProfiles = new SubCollection<Profile>();

    @Collection<Profile>({
        namespace: "Profile",
        backlistFields: {
            owners: true,
            users: true,
            posts: true,
            tasks: true,
            socialProfiles: true,
            reviews: true,
        },
    })
    @SecuritySubCollection({
        get: `${isAdmin} || request.auth.uid == UserMetadataId`,
        list: "true",
        create: isAdmin,
        update: isAdmin,
        delete: `request.auth.uid == UserMetadataId || exists(/databases/$(database)/documents/Profile/$(ProfileId)/owners/$(request.auth.uid)) || ${isAdmin}`,
    })
    ownerProfiles = new SubCollection<Profile>();

    get isDeleted() {
        return this.deletedAt !== null;
    }

    toString() {
        if (
            this.firstName !== undefined &&
            this.firstName.length > 0 &&
            this.lastName !== undefined &&
            this.lastName.length > 0
        )
            return `${this.firstName} ${this.lastName}`;
        else if (this.phoneNumber !== undefined && this.phoneNumber.length > 0)
            return this.phoneNumber;
        else if (this.email !== undefined && this.email.length > 0) return this.email;
        return "";
    }
}
